import type { ReactNode } from "react";
import { createContext, useContext, useState, useCallback } from "react";
import Toast from "./Toast";
import type { ToastType } from "./Toast";

type ToastMessage = {
  id: number;
  message: string;
  type: ToastType;
};

type ToastContextType = {
  showToast: (message: string, type?: ToastType) => void;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  showInfo: (message: string) => void;
};

const ToastContext: React.Context<ToastContextType> = createContext<ToastContextType>({
  showToast: () => {},
  showError: () => {},
  showSuccess: () => {},
  showInfo: () => {}
});

type Props = {
  children: ReactNode;
};

let toastId = 0;

export const ToastProvider = ({ children }: Props): ReactNode => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((message: string, type: ToastType = "info") => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const showError = useCallback(
    (message: string) => {
      showToast(message, "error");
    },
    [showToast]
  );

  const showSuccess = useCallback(
    (message: string) => {
      showToast(message, "success");
    },
    [showToast]
  );

  const showInfo = useCallback(
    (message: string) => {
      showToast(message, "info");
    },
    [showToast]
  );

  const value: ToastContextType = {
    showToast,
    showError,
    showSuccess,
    showInfo
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.map((toast) => (
        <Toast key={toast.id} message={toast.message} type={toast.type} onDismiss={() => removeToast(toast.id)} />
      ))}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  return useContext<ToastContextType>(ToastContext);
};

export default ToastContext;
